import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { List, Kanban as KanbanIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { TicketList } from "./TicketList";
import { TicketKanban } from "./TicketKanban";
import { NuevoTicketDialog } from "./NuevoTicketDialog";
import { TicketDetailDialog } from "./TicketDetailDialog";
import { TicketStatsPanel } from "./TicketStatsPanel";
import { splitArchived, ARCHIVE_AFTER_DAYS } from "./ticketArchive";
import {
  listarMisTickets,
  listarTodosTickets,
  obtenerEstadisticas,
  type Ticket,
  type TicketStats,
} from "../../services/ticketsService";
import { useUserRol } from "../../App";

type VistaTickets = "lista" | "kanban";

// "Archivero": los tickets cerrados hace más de ARCHIVE_AFTER_DAYS se
// dejan de mostrar en Mis tickets/Todos y solo aparecen en su propia
// pestaña -- ver ticketArchive.ts y TICKETS-API.md § "Archivero
// (convención de UI)".
export function TicketsView() {
  const rol = useUserRol();
  const isAdmin = rol === "ADMIN";

  const [tab, setTab] = useState("mios");
  const [vista, setVista] = useState<VistaTickets>("lista");
  const [misTickets, setMisTickets] = useState<Ticket[]>([]);
  const [todosTickets, setTodosTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loadingMios, setLoadingMios] = useState(true);
  const [loadingTodos, setLoadingTodos] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const cargarMios = useCallback(() => {
    setLoadingMios(true);
    listarMisTickets()
      .then(setMisTickets)
      .catch((err: any) => toast.error("No se pudieron cargar tus tickets: " + err.message))
      .finally(() => setLoadingMios(false));
  }, []);

  const cargarTodos = useCallback(() => {
    if (!isAdmin) return;
    setLoadingTodos(true);
    listarTodosTickets()
      .then(setTodosTickets)
      .catch((err: any) => toast.error("No se pudieron cargar los tickets: " + err.message))
      .finally(() => setLoadingTodos(false));
  }, [isAdmin]);

  const cargarStats = useCallback(() => {
    if (!isAdmin) return;
    setLoadingStats(true);
    obtenerEstadisticas()
      .then(setStats)
      .catch((err: any) => toast.error("No se pudieron cargar las estadísticas: " + err.message))
      .finally(() => setLoadingStats(false));
  }, [isAdmin]);

  useEffect(() => {
    cargarMios();
    cargarTodos();
    cargarStats();
  }, [cargarMios, cargarTodos, cargarStats]);

  const refrescarTodo = () => {
    cargarMios();
    if (isAdmin) {
      cargarTodos();
      cargarStats();
    }
  };

  const { activos: misActivos, archivados: misArchivados } = useMemo(() => splitArchived(misTickets), [misTickets]);
  const { activos: todosActivos, archivados: todosArchivados } = useMemo(() => splitArchived(todosTickets), [todosTickets]);

  // El Archivero de un admin usa todosTickets (superset que ya incluye
  // los suyos); el de un reportero normal usa misTickets, lo único que tiene.
  const archivados = isAdmin ? todosArchivados : misArchivados;
  const loadingArchivero = isAdmin ? loadingTodos : loadingMios;

  const renderTickets = (tickets: Ticket[], loading: boolean, showReporter: boolean, emptyMessage: string) => {
    if (loading) {
      return <div className="text-muted-foreground animate-pulse py-8 text-center">Cargando tickets...</div>;
    }
    if (vista === "kanban") {
      return <TicketKanban tickets={tickets} showReporter={showReporter} onSelect={setSelectedTicketId} />;
    }
    return (
      <TicketList
        tickets={tickets}
        loading={false}
        showReporter={showReporter}
        emptyMessage={emptyMessage}
        onSelect={setSelectedTicketId}
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1>Tickets</h1>
          <p className="text-muted-foreground">Reporta bugs y sigue su avance.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-md p-0.5">
            <Button
              type="button"
              variant={vista === "lista" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2"
              onClick={() => setVista("lista")}
              title="Vista de lista"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant={vista === "kanban" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2"
              onClick={() => setVista("kanban")}
              title="Vista de tablero"
            >
              <KanbanIcon className="h-4 w-4" />
            </Button>
          </div>
          <NuevoTicketDialog onCreated={refrescarTodo} />
        </div>
      </div>

      {isAdmin && (loadingStats || !stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[68px] rounded-lg border bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <TicketStatsPanel stats={stats} />
      ))}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mios">Mis tickets</TabsTrigger>
          {isAdmin && <TabsTrigger value="todos">Todos</TabsTrigger>}
          <TabsTrigger value="archivero">Archivero</TabsTrigger>
        </TabsList>
        <TabsContent value="mios">
          {renderTickets(misActivos, loadingMios, false, "No has reportado ningún ticket todavía.")}
        </TabsContent>
        {isAdmin && (
          <TabsContent value="todos">
            {renderTickets(todosActivos, loadingTodos, true, "No hay tickets reportados todavía.")}
          </TabsContent>
        )}
        <TabsContent value="archivero">
          <p className="text-xs text-muted-foreground mb-2">
            Tickets cerrados hace más de {ARCHIVE_AFTER_DAYS} días. Los cerrados recientes siguen en{" "}
            {isAdmin ? '"Mis tickets" / "Todos"' : '"Mis tickets"'}.
          </p>
          <TicketList
            tickets={archivados}
            loading={loadingArchivero}
            showReporter={isAdmin}
            emptyMessage="No hay tickets archivados."
            onSelect={setSelectedTicketId}
          />
        </TabsContent>
      </Tabs>

      <TicketDetailDialog
        ticketId={selectedTicketId}
        onOpenChange={(open) => !open && setSelectedTicketId(null)}
        onChanged={refrescarTodo}
      />
    </div>
  );
}
